<script setup lang="ts">
// Working-directory picker for the new-session form. Two ways to pick the
// same value:
//   - desktop: type a path, press Tab to complete against the agent's FS
//     (dir.list RPC).
//   - mobile / anyone: tap the folder button to browse — list sub-dirs, tap
//     to descend, "↑" to go up, "Use this folder" to pick the current dir.
// Both modes drive the same v-model string (an absolute path or "" = default).
import { ref } from "vue";
import { useI18n } from "vue-i18n";

import { api, ApiError } from "@/api/client";
import type { DirListReply } from "@/api/types";

const props = defineProps<{
  endpointId: string;
  targetType: "agent" | "nx";
  modelValue: string;
  disabled?: boolean;
}>();
const emit = defineEmits<{ "update:modelValue": [value: string] }>();

const { t } = useI18n();

// Same dir.list RPC on both products; pick the matching REST route.
function fetchDir(path?: string) {
  return props.targetType === "nx"
    ? api.nx.dirList(props.endpointId, path)
    : api.agents.dirList(props.endpointId, path);
}

const open = ref(false);
const loading = ref(false);
const error = ref<string | null>(null);
const listing = ref<DirListReply | null>(null);
const showHidden = ref(false);

function setValue(v: string) {
  emit("update:modelValue", v);
}

function describe(e: unknown): string {
  if (e instanceof ApiError) return `${e.code} (HTTP ${e.status})`;
  if (e instanceof Error) return e.message;
  return "error";
}

// Load a directory listing into the browser panel. `path` undefined = home.
async function browse(path?: string) {
  if (!props.endpointId) return;
  loading.value = true;
  error.value = null;
  try {
    listing.value = await fetchDir(path);
  } catch (e) {
    error.value = describe(e);
  } finally {
    loading.value = false;
  }
}

async function openBrowser() {
  if (props.disabled || !props.endpointId) return;
  open.value = true;
  const start = props.modelValue.trim();
  await browse(start || undefined);
  // A partial/invalid path (mid-typing) fails — fall back to home.
  if (error.value) await browse(undefined);
}

function closeBrowser() {
  open.value = false;
}

async function descend(path: string) {
  setValue(path);
  await browse(path);
}

async function goUp() {
  const parent = listing.value?.parent;
  if (parent) await descend(parent);
}

function useCurrent() {
  if (listing.value) setValue(listing.value.path);
  open.value = false;
}

// Tab-completion: split the typed value into dir + prefix, list the dir, and
// complete to the single match (or open the browser on the dir for many).
async function complete() {
  if (props.disabled || !props.endpointId) return;
  const val = props.modelValue.trim();
  if (!val.startsWith("/")) {
    await openBrowser();
    return;
  }
  const slash = val.lastIndexOf("/");
  const dir = slash <= 0 ? "/" : val.slice(0, slash);
  const prefix = val.slice(slash + 1);
  loading.value = true;
  error.value = null;
  try {
    const reply = await fetchDir(dir);
    const matches = reply.entries.filter((e) => e.name.startsWith(prefix));
    if (matches.length === 1) {
      setValue(matches[0].path);
    } else if (matches.length > 1) {
      listing.value = reply;
      open.value = true;
    }
  } catch (e) {
    error.value = describe(e);
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <div class="dir">
    <div class="dir__row">
      <input
        :value="modelValue"
        :disabled="disabled"
        placeholder="/home/dragon"
        spellcheck="false"
        autocapitalize="off"
        autocomplete="off"
        @input="setValue(($event.target as HTMLInputElement).value)"
        @keydown.tab.prevent="complete"
      />
      <button
        type="button"
        class="dir__toggle"
        :disabled="disabled || !endpointId"
        :aria-expanded="open"
        :title="t('new.dir_browse')"
        :aria-label="t('new.dir_browse')"
        @click="open ? closeBrowser() : openBrowser()"
      >
        🗀
      </button>
    </div>

    <div v-if="open" class="dir__panel">
      <div class="dir__head">
        <span class="dir__cwd">{{ listing?.path || "…" }}</span>
        <button type="button" class="dir__x" :aria-label="t('a11y.close')" @click="closeBrowser">
          ✕
        </button>
      </div>

      <p v-if="error" class="dir__error">{{ error }}</p>
      <p v-else-if="loading" class="dir__muted">{{ t("new.dir_loading") }}</p>

      <ul v-else class="dir__list">
        <li v-if="listing?.parent">
          <button type="button" class="dir__item dir__up" @click="goUp">↑ ..</button>
        </li>
        <li
          v-for="e in (listing?.entries || []).filter((x) => showHidden || !x.hidden)"
          :key="e.path"
        >
          <button type="button" class="dir__item" @click="descend(e.path)">
            <span class="dir__folder">🗀</span>{{ e.name }}
          </button>
        </li>
        <li v-if="listing && listing.entries.filter((x) => showHidden || !x.hidden).length === 0">
          <span class="dir__muted">{{ t("new.dir_empty") }}</span>
        </li>
      </ul>

      <div class="dir__foot">
        <label class="dir__hidden">
          <input v-model="showHidden" type="checkbox" />
          {{ t("new.dir_hidden") }}
        </label>
        <button type="button" class="dir__use" :disabled="!listing" @click="useCurrent">
          {{ t("new.dir_choose") }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.dir {
  position: relative;
}
.dir__row {
  display: flex;
  gap: 0.4rem;
}
.dir__row input {
  flex: 1;
  min-width: 0;
  padding: 0.5rem 0.7rem;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  background: var(--color-bg);
  color: var(--color-fg);
  font-family: var(--font-mono);
  font-size: 0.95rem;
}
.dir__toggle {
  flex: 0 0 auto;
  width: 2.6rem;
  min-height: 44px;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  background: var(--color-bg);
  color: var(--color-fg);
  cursor: pointer;
  font-size: 1.1rem;
}
.dir__toggle:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.dir__panel {
  margin-top: 0.4rem;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background: var(--color-bg-elev);
  overflow: hidden;
}
.dir__head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.6rem;
  border-bottom: 1px solid var(--color-border);
}
.dir__cwd {
  flex: 1;
  min-width: 0;
  font-family: var(--font-mono);
  font-size: 0.78rem;
  color: var(--color-fg-dim);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  direction: rtl;
  text-align: left;
}
.dir__x {
  flex: 0 0 auto;
  border: none;
  background: transparent;
  color: var(--color-fg-dim);
  cursor: pointer;
  font-size: 0.9rem;
}
.dir__list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 14rem;
  overflow-y: auto;
}
.dir__item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.55rem 0.6rem;
  border: none;
  border-bottom: 1px solid var(--color-border);
  background: transparent;
  color: var(--color-fg);
  cursor: pointer;
  text-align: left;
  font-family: var(--font-mono);
  font-size: 0.85rem;
  min-height: 44px;
}
.dir__item:hover {
  background: var(--color-bg);
}
.dir__up {
  color: var(--color-fg-dim);
}
.dir__folder {
  opacity: 0.7;
}
.dir__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  padding: 0.5rem 0.6rem;
}
.dir__hidden {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.78rem;
  color: var(--color-fg-dim);
}
.dir__use {
  padding: 0.4rem 0.85rem;
  background: var(--color-accent);
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
}
.dir__use:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.dir__error {
  margin: 0;
  padding: 0.5rem 0.6rem;
  font-size: 0.8rem;
  color: var(--color-error);
}
.dir__muted {
  margin: 0;
  padding: 0.5rem 0.6rem;
  font-size: 0.8rem;
  color: var(--color-fg-dim);
}
</style>
